import type { PlatformConfig } from "./service-platform-types";
import { AVATAR_WEBP } from "@/lib/optimizedImages";
import {
  AlertTriangle,
  LineChart,
  Users,
  Layers,
  Database,
  ArrowRight,
  GitBranch,
  Radio,
  Share2,
  Workflow,
  Sparkles,
  Bot,
  Gauge,
  PhoneCall,
  Activity,
  PhoneForwarded,
  ShieldAlert,
  Server,
  Zap,
  Package,
  Wrench,
  Boxes,
  HelpCircle,
  Clock,
  Briefcase,
  DollarSign,
  TrendingUp,
  FileSpreadsheet,
  CheckCircle2,
  Calendar,
  Code2,
  Cpu,
  GraduationCap,
  ShieldCheck,
  Search,
  Gift,
  Plug,
  BarChart3,
  Settings,
  Building2,
  LayoutGrid,
  Mail,
  CalendarClock,
  Star,
  CreditCard,
  FileSearch,
  ClipboardList,
} from "lucide-react";

export const gohighlevelConfig: PlatformConfig = {
  platformName: "GoHighLevel",
  eyebrow: "CRM REVOPS ARCHITECT",
  ghostWord: "REVOPS",
  architectureGhostWord: "ARCHITECTURE",

  heroHeadlineNavy: "Stop Patching GoHighLevel.",
  heroHeadlineBlue: "Start Architecting It.",
  heroAuthorRole: "RevOps Architect",
  heroPara1:
    "I engineer multi-account snapshots, automated onboarding, and lead-to-cash pipelines for agencies that refuse to babysit broken automations.",
  heroPara2: "300+ sub-accounts built. Zero account managers between us.",
  heroCtaLabel: "Book a Systems Audit",
  heroVideoSrc:
    "https://assets.cdn.filesafe.space/j1oGTjseMx0O1vAMfaCU/media/6a4e898c8b929b11f2e756ab.mp4",
  heroVideoPoster:
    "https://assets.cdn.filesafe.space/j1oGTjseMx0O1vAMfaCU/media/6abbae84735e4fa2a0085f40.webp",
  heroVideoAlt: "Video walkthrough of a GoHighLevel RevOps architecture build",
  // Flagship page — the only service page with a real walkthrough video.
  heroVideoPlayable: true,
  heroTrustHeader: "Trusted by High-Growth Agencies & Enterprises",

  painEyebrow: "IS THIS YOUR GOHIGHLEVEL?",
  painHeadlineNavy: "The Symptoms of a",
  painHeadlineBlue: "System Built Wrong.",
  painSub:
    "If two or more of these feel familiar, the issue usually isn't another workflow. It's the architecture underneath it.",
  symptoms: [
    {
      icon: AlertTriangle,
      title: "Snapshots break on new signups",
      desc: "Your snapshots deploy on demo day and break the moment a new client is added.",
    },
    {
      icon: LineChart,
      title: "Pipeline reporting is never accurate",
      desc: "No matter how many fields you add, the numbers never reconcile with reality.",
    },
    {
      icon: Users,
      title: "Onboarding is 14 manual steps",
      desc: "Client onboarding is a manual checklist — and someone always forgets one.",
    },
    {
      icon: Settings,
      title: "SaaS mode half-works",
      desc: "Billing fires, but the sub-account isn't provisioned. Half a system.",
    },
    {
      icon: ShieldCheck,
      title: "Three 'experts' who were task-takers",
      desc: "You've hired people who configured tasks one at a time instead of architecting.",
    },
    {
      icon: Building2,
      title: "Growth outpaces your system",
      desc: "Your agency is scaling, but your CRM architecture can't keep up.",
    },
  ],
  painCtaLabel: "Get a Systems Audit",
  painCtaCopy: "If 2+ symptoms hit, let's audit the architecture underneath.",

  archEyebrow: "WHAT I BUILD",
  archHeadlineNavy: "Production-Grade",
  archHeadlineBlue: "GoHighLevel Architecture.",
  archSub:
    "— the version-controlled infrastructure that scales across hundreds of sub-accounts without breaking.",
  archSubEmphasis: "Not configuration. Engineering",
  archProofShots: [
    {
      label: "AI Agent Command Center",
      tag: "01 • AI SYSTEM",
      desc: "Autonomous qualification, follow-up and routing at scale.",
      image:
        "https://storage.googleapis.com/msgsndr/pLFrnXnIu7IgQ4WCaCfz/media/d58352c3-1bdf-4727-aadf-eb65e4051977.png",
    },
    {
      label: "Multi-Channel Lead Conversion",
      tag: "02 • LEAD GENERATION",
      desc: "Unified lead capture, enrichment and routing across every channel.",
      image:
        "https://storage.googleapis.com/msgsndr/pLFrnXnIu7IgQ4WCaCfz/media/245c1411-0d59-49de-868a-b734105d5dae.png",
    },
    {
      label: "Customer Lifecycle Automation",
      tag: "03 • CLIENT LIFECYCLE",
      desc: "End-to-end automation from onboarding to renewal.",
      image:
        "https://storage.googleapis.com/msgsndr/pLFrnXnIu7IgQ4WCaCfz/media/4d74ac9c-4b0d-401d-a9ed-4560b8107502.png",
    },
    {
      label: "Executive BI Dashboard",
      tag: "04 • ANALYTICS",
      desc: "Real-time performance, visibility and decision-ready insights.",
      image:
        "https://storage.googleapis.com/msgsndr/pLFrnXnIu7IgQ4WCaCfz/media/19ffa953-83ba-4397-88a9-3fd2520e7142.png",
    },
  ],
  archUnderneathLabel: "THE ARCHITECTURE UNDERNEATH",
  archUnderHeadlineNavy: "Six systems that",
  archUnderHeadlineBlue: "make the whole thing scale.",
  capabilities: [
    {
      icon: Layers,
      title: "300+ Sub-Accounts Deployed",
      desc: "Version-controlled snapshots with Custom Values that deploy across unlimited sub-accounts bug-free.",
    },
    {
      icon: GitBranch,
      title: "SaaS Mode Architecture",
      desc: "Stripe plan wiring, provisioning automation, invoice logic, and client billing at scale.",
    },
    {
      icon: Users,
      title: "Automated Client Onboarding",
      desc: "Zero-touch provisioning, welcome sequences, credentials handoff, and time-to-value acceleration.",
    },
    {
      icon: LineChart,
      title: "Lead-to-Cash Pipelines",
      desc: "Meta Conversion API, attribution triggers, deal-stage automation, and SLA alerts.",
    },
    {
      icon: Settings,
      title: "AI Agent Integration",
      desc: "Conversational AI inside workflows — inbound qualification, booking automation, and routing.",
    },
    {
      icon: Building2,
      title: "Multi-Location Franchise Logic",
      desc: "Per-location pipelines, roll-up reporting, and brand-consistent snapshots across every site.",
    },
  ],
  archConclusionText:
    "Version-controlled. Multi-account. Built for production.",
  archConclusionHighlight: "Built for production.",
  archShotAltSuffix: "GoHighLevel architecture screenshot",

  costEyebrow: "The Real Cost",
  costHeadlineNavy: "What Broken GoHighLevel",
  costHeadlineBlue: "Actually Costs You.",
  costs: [
    {
      stat: "3-5 clients/month",
      desc: "churn from broken onboarding — roughly $1,500-2,500 MRR bleeding every month.",
    },
    {
      stat: "8+ hours/week",
      desc: "spent fixing automations manually — over $25K/year of your time gone.",
    },
    {
      stat: "Zero audit trail",
      desc: "when a sub-account breaks, no one knows why it failed or how to prevent it again.",
    },
  ],
  costConclusion:
    "Every month you wait, the debt compounds. An architect fixes the root — not the symptom.",

  processEyebrow: "The Build Process",
  processHeadlineNavy: "How I Architect Your",
  processHeadlineBlue: "RevOps System.",
  processSub:
    "A 4-phase process from audit to scale. No black boxes — you approve before I touch production.",
  processSteps: [
    {
      title: "Systems Audit",
      timeline: "3–5 days",
      desc: "I map your current CRM, identify pipeline leaks, and document every manual touchpoint that should be automated. Delivered as a written report.",
    },
    {
      title: "Architecture Design",
      timeline: "5–10 days",
      desc: "Custom field schema, pipeline logic, snapshot blueprint, and automation map — designed before any build. You approve before I touch production.",
    },
    {
      title: "Build & Deploy",
      timeline: "10–20 days",
      desc: "Snapshot engineering, automated onboarding, lead-to-cash pipelines, and reporting dashboards. Version-controlled and tested.",
    },
    {
      title: "Scale & Maintain",
      timeline: "Ongoing",
      desc: "Ongoing maintenance, snapshot updates, and new automation builds as you grow from 5 to 50+ clients. A documented learning loop, not a black box.",
    },
  ],
  processCtaCopy: "Stop the bleed. Start with a free systems audit.",
  processCtaLabel: "Start the Audit",

  migrationEyebrow: "System Rescue & Migration",
  migrationHeadlineNavy: "Inherit a Broken Build?",
  migrationHeadlineBlue: "I Audit Before I Touch It.",
  migrationSub:
    "Most of my engagements start with a half-built system another freelancer left behind. Here's how I take it over without anything breaking.",
  migrationSteps: [
    {
      icon: FileSearch,
      title: "Map",
      desc: "I document every workflow, field, pipeline, and tag in your current account before touching anything. No surprises, no silent deletions.",
    },
    {
      icon: ClipboardList,
      title: "Document",
      desc: "A written architecture report of what exists, what's broken, and what must be preserved — so you own the map of your own system.",
    },
    {
      icon: Wrench,
      title: "Rebuild",
      desc: "I engineer the corrected architecture in a controlled snapshot — version-controlled and tested against your real data shapes.",
    },
    {
      icon: CheckCircle2,
      title: "Verify",
      desc: "Migration completes only after a field-by-field verification pass. Zero data loss is the standard, not a hope.",
    },
  ],
  migrationConclusionPre:
    "Zero data loss isn't a promise. It's a 4-step process that starts with",
  migrationConclusionBlue: "documentation, not deletion.",
  migrationCtaCopy:
    "Inheriting a broken build? I audit before I touch anything.",
  migrationCtaLabel: "Audit My System",

  testimonialsEyebrow: "Client Results",
  testimonialsHeadlineNavy: "What Happens After",
  testimonialsHeadlineBlue: "the Architecture.",
  testimonials: [
    {
      name: "David L.",
      title: "VP Operations, SaaS Company",
      image:
        "https://vibe.filesafe.space/1790273900671562476/assets/ea773865-76e8-4fcf-a408-ce29bf6cfc65.png",
      text: "Lead response went from hours to seconds. We booked 40% more demos in the first month because no lead went cold. Highest ROI hire this year.",
      metric: "40%",
      metricLabel: "MORE DEMOS BOOKED",
    },
    {
      name: "Kevin P.",
      title: "Sales Director, Insurance Brokerage",
      image:
        "https://vibe.filesafe.space/1790273900671562476/assets/1da6c5ea-e593-4890-934a-b7da49e32c41.png",
      text: "CRM is finally a revenue driver, not just a database. Our pipeline visibility went from zero to real-time dashboards. Closed-won rate up 23% in one quarter.",
      metric: "23%",
      metricLabel: "CLOSED-WON RATE LIFT",
    },
    {
      name: "Maria F.",
      title: "CEO, E-commerce Brand",
      image:
        "https://vibe.filesafe.space/1790273900671562476/assets/e44983c6-a240-4684-a595-95a725bd8c95.png",
      text: "Decision velocity tripled. He is the architect behind our scaling from $200K to $600K monthly revenue. The systems he built still run without him.",
      metric: "$200K → $600K",
      metricLabel: "MONTHLY REVENUE SCALED",
    },
  ],
  testimonialsCtaCopy: "Be the next agency running clean architecture.",
  testimonialsCtaLabel: "Book a call",

  buildsEyebrow: "What I Build Inside GoHighLevel",
  buildsHeadlineNavy: "Every Layer of the",
  buildsHeadlineBlue: "Revenue System.",
  buildsSub:
    "From snapshot deployment to executive dashboards — the full build surface I architect and own end-to-end.",
  builds: [
    { icon: Layers, label: "Snapshot Engineering" },
    { icon: GitBranch, label: "SaaS / White-Label Mode" },
    { icon: LayoutGrid, label: "Custom Pipelines" },
    { icon: Settings, label: "Workflow Automations" },
    { icon: Users, label: "Funnels & Landing Pages" },
    { icon: Mail, label: "Email / SMS Sequences" },
    { icon: CalendarClock, label: "Calendars & Booking" },
    { icon: BarChart3, label: "Reporting Dashboards" },
    { icon: Star, label: "Reputation Management" },
    { icon: Bot, label: "AI Chatbots & Agents" },
    { icon: CreditCard, label: "Invoicing & Payments" },
    { icon: Building2, label: "Membership Sites" },
  ],
  buildsIntegrationsLabel: "Integrations I Wire Into GoHighLevel",
  integrations: [
    "Twilio",
    "Stripe",
    "Meta / Facebook Ads",
    "Google Ads",
    "Zapier",
    "Make.com",
    "n8n",
    "Close CRM",
    "HubSpot",
    "ChatGPT / OpenAI",
    "Claude",
    "Slack",
  ],
  buildsCtaCopy: "Tell me which layers you need architected.",
  buildsCtaLabel: "Book a call",

  snapshotsEyebrow: "Snapshot Library",
  snapshotsHeadlineNavy: "Ready-to-Deploy",
  snapshotsHeadlineBlue: "Architectures.",
  snapshotsSub:
    "A preview of the version-controlled snapshots I engineer and deploy — not templates copied from a marketplace.",
  snapshots: [
    {
      name: "Agency Onboarding Snapshot",
      tag: "White-Label",
      desc: "Zero-touch client provisioning — welcome sequence, credentials handoff, calendar setup, and first-30-day nurture wired in.",
    },
    {
      name: "SaaS Mode Starter Snapshot",
      tag: "Resellers",
      desc: "Stripe plan mapping, sub-account provisioning, invoice logic, and re-billing wiring — deployable across unlimited accounts.",
    },
    {
      name: "Lead-to-Cash Snapshot",
      tag: "Revenue Engine",
      desc: "Meta Conversion API, attribution triggers, deal-stage automation, SLA alerts, and closed-won reporting in one architecture.",
    },
    {
      name: "Franchise Roll-Up Snapshot",
      tag: "Multi-Location",
      desc: "Per-location pipelines with brand-consistent automations and HQ roll-up reporting across every site.",
    },
  ],
  snapshotsCtaCopy: "Get a snapshot engineered for 100+ sub-accounts.",
  snapshotsCtaLabel: "Engineer My Snapshot",

  industriesEyebrow: "Industries I Build For",
  industriesHeadlineNavy: "Architecture Tuned to",
  industriesHeadlineBlue: "Your Business Model.",
  industriesSub:
    "The same engineering principles, adapted to the way each business model actually earns revenue inside GoHighLevel.",
  industries: [
    {
      name: "Marketing Agencies",
      builds: [
        "White-label SaaS-mode snapshots",
        "Client onboarding automation",
        "Roll-up reporting across sub-accounts",
      ],
    },
    {
      name: "SaaS-Mode Resellers",
      builds: [
        "Stripe plan + provisioning wiring",
        "Version-controlled snapshot marketplace",
        "Billing & invoice logic at scale",
      ],
    },
    {
      name: "Multi-Location Franchises",
      builds: [
        "Per-location pipelines",
        "Brand-consistent snapshots",
        "HQ roll-up reporting dashboards",
      ],
    },
    {
      name: "Coaching Businesses",
      builds: [
        "Calendar & booking architecture",
        "Membership site delivery",
        "Lead nurture to high-ticket close",
      ],
    },
  ],
  industriesCtaCopy: "See your industry's pipeline mapped end-to-end.",
  industriesCtaLabel: "Map My Pipeline",

  vettingEyebrow: "How to Vet a CRM Architect",
  vettingHeadlineNavy: "The 6 Criteria That",
  vettingHeadlineBlue: "Actually Matter.",
  vettingSub:
    "Most agencies hire on price and regret on performance. Use this scorecard before you pay anyone to touch your GoHighLevel.",
  criteria: [
    {
      label: "CRM Implementation",
      what: "Can they architect the entire platform — not just create a few fields?",
      score: "Full snapshot + field-schema + pipeline architecture",
    },
    {
      label: "Funnel & Conversion Strategy",
      what: "Do they understand why funnels convert, or do they just drag elements?",
      score: "Conversion-led funnel engineering backed by buyer psychology",
    },
    {
      label: "Automation Capability",
      what: "Can they build conditional, multi-step logic — not just triggers?",
      score: "Version-controlled workflows with branching + SLA alerts",
    },
    {
      label: "Specialist Expertise",
      what: "Are they a certified architect or a generalist wearing many hats?",
      score: "GoHighLevel & HubSpot specialist, not a task-taker",
    },
    {
      label: "Scope Transparency",
      what: "Do they tell you exactly what they will and won't build — before you pay?",
      score:
        "Written audit + approved architecture before any production touch",
    },
    {
      label: "Post-Launch Support",
      what: "Who maintains the system after launch — and is it documented?",
      score: "Retained maintenance with a documented learning loop",
    },
  ],
  vaComparison: [
    {
      capability: "Snapshot Engineering",
      va: "Cannot build — copies templates manually",
      architect: "Custom, version-controlled, deploys across 100+ sub-accounts",
    },
    {
      capability: "Custom Field Architecture",
      va: "Creates single fields on request",
      architect: "Designs full relational schema before any build",
    },
    {
      capability: "Lead-to-Cash Automation",
      va: "Task-by-task, reactive fixes",
      architect: "End-to-end pipeline with attribution + SLA triggers",
    },
    {
      capability: "Multi-Account Deployment",
      va: "One account at a time, by hand",
      architect: "Snapshot logic + Custom Values, no manual mapping",
    },
    {
      capability: "Engagement Model",
      va: "Task-taker waiting for instructions",
      architect: "Direct engineer who owns outcomes, no middlemen",
    },
    {
      capability: "Documentation",
      va: "None — the system lives in their head",
      architect: "Documented, structured, transferable from day one",
    },
  ],
  vettingCtaCopy: "Vet me against this checklist on a call.",
  vettingCtaLabel: "Vet Me on a Call",

  pricingEyebrow: "Pricing & Value",
  pricingHeadlineNavy: "RevOps Architect vs. General VA vs.",
  pricingHeadlineBlue: "US Agency.",
  pricingIntro:
    "A US CRM agency charges a premium retainer. A general VA charges less but can't architect. A senior RevOps architect delivers the full architecture at a fraction — scoped per project or retainer.",
  pricingIntroHighlight: "Book a free call for a custom quote.",
  pricingRows: [
    {
      capability: "Snapshot engineering",
      architect: "Custom, version-controlled",
      va: "Cannot build",
      agency: "Yes, at premium rate",
    },
    {
      capability: "Custom field architecture",
      architect: "Full schema design",
      va: "Basic field creation",
      agency: "Yes, at premium",
    },
    {
      capability: "Lead-to-cash automation",
      architect: "End-to-end pipeline",
      va: "Task-by-task only",
      agency: "Yes, project-based",
    },
    {
      capability: "Multi-account deployment",
      architect: "100+ sub-accounts",
      va: "Manual, one at a time",
      agency: "Yes, at high cost",
    },
    {
      capability: "Engagement model",
      architect: "Direct engineer, no middlemen",
      va: "Task-taker, reactive",
      agency: "Account-managed team",
    },
    {
      capability: "Ongoing maintenance",
      architect: "Documented learning loop",
      va: "Ad hoc, reactive",
      agency: "Retainer required",
    },
  ],

  freeAuditBadge: "ZERO-RISK DISCOVERY SESSION",
  freeAuditHeadlinePre: "Before You Pay Anyone —",
  freeAuditHeadlineBlue: "Let Me Audit Your CRM.",
  freeAuditDesc:
    "On a focused 30-minute working call, I'll map your current pipelines, uncover hidden revenue leaks, and show you exactly what needs to be architected for production scale. If I'm not the best fit, I'll point you directly to who is.",
  freeAuditDeliverables: [
    {
      title: "Pipeline Leak Map",
      desc: "Every stage where leads slip through, lack tracking, or lack automated follow-up.",
      tag: "DIAGNOSTIC",
    },
    {
      title: "Snapshot & Automation Health Check",
      desc: "Code review of custom fields, tag naming, trigger loops, and webhook failures.",
      tag: "CODE REVIEW",
    },
    {
      title: "90-Day Architecture Roadmap",
      desc: "Prioritized engineering plan: what to patch today, rebuild next, and automate at scale.",
      tag: "ACTION PLAN",
    },
  ],
  freeAuditCtaLabel: "Book the Free Systems Audit",

  whoEyebrow: "Who I Am",
  whoHeadlineNavy: "A Systems Engineer,",
  whoHeadlineBlue: "Not a Task-Taker.",
  whoName: "Marjohn Robillo",
  whoRole: "Revenue Operations Architect",
  whoBio:
    "I came from disciplined environments where documentation wasn't optional. I bring that same standard to every GoHighLevel build — documented, structured, and transferable. 5+ years, 300+ systems, four continents.",
  whoImage: AVATAR_WEBP,
  whoImageAlt: "Marjohn Robillo — Revenue Operations Architect",
  credentials: [
    "MPA Graduate",
    "Licensed Professional Teacher (LPT)",
    "MUN-UNESCO Diplomat · Bali 2022",
    "Licensed Financial Advisor · PRULife UK",
    "Former CO1 · Bureau of Corrections PH",
    "GoHighLevel & HubSpot Specialist",
  ],
  whoCtaCopy: "Book a call directly with me — no account manager in between.",
  whoCtaLabel: "Book a Call",

  casesEyebrow: "Related Architecture Guides",
  casesHeadlineNavy: "Architecture",
  casesHeadlineBlue: "Guides.",
  caseStudies: [
    {
      to: "/blog/gohighlevel-specialist-philippines",
      title: "Why General VAs Fail at Advanced GHL Operations",
      desc: "The difference between a task-taker and a true RevOps architect.",
    },
    {
      to: "/blog/how-to-automate-client-onboarding-gohighlevel",
      title: "Automating Client Onboarding in GoHighLevel",
      desc: "Engineering a frictionless, zero-touch welcome sequence.",
    },
    {
      to: "/blog/ghl-automation-expert-philippines",
      title: "Hiring a GHL Automation Expert",
      desc: "What to look for when you need architecture, not configuration.",
    },
    {
      to: "/blog/hire-technical-va-gohighlevel",
      title: "Hiring a Technical VA for GoHighLevel",
      desc: "The skill set that separates engineers from generalists.",
    },
  ],

  faqEyebrow: "Frequently Asked",
  faqHeadlineNavy: "Questions Before",
  faqHeadlineBlue: "Hiring Me.",
  faqs: [
    {
      question: "What does a GoHighLevel RevOps architect actually do?",
      answer:
        "A RevOps architect designs and builds the entire revenue system inside GoHighLevel: custom field schemas, pipeline logic, automated onboarding sequences, snapshot deployments across sub-accounts, lead-to-cash automation, and reporting dashboards. They engineer the infrastructure, not just configure it.",
    },
    {
      question: "How much does a GoHighLevel RevOps architect cost?",
      answer:
        "Engagements are scoped per project or retainer. A senior RevOps architect delivers the architecture at a fraction of a US agency rate. Book a free systems audit call to receive a custom scope and quote for your GoHighLevel needs.",
    },
    {
      question: "How many GoHighLevel sub-accounts have you built?",
      answer:
        "300+ GoHighLevel sub-accounts across US, Canadian, and Australian agencies — including multi-account snapshot deployments, custom onboarding automations, lead-to-cash pipelines, and Meta Conversion API implementations.",
    },
    {
      question: "Can you deploy snapshots across 100+ sub-accounts?",
      answer:
        "Yes. I engineer snapshots using Custom Values and conditional logic so they deploy cleanly across unlimited sub-accounts without manual field mapping — bug-free, scalable, and version-controlled.",
    },
    {
      question: "Do you offer ongoing maintenance after the build?",
      answer:
        "Yes. Most clients retain me on a monthly maintenance contract for workflow monitoring, snapshot updates, and new automation builds as their agency scales. The system gets better every month through a documented learning loop.",
    },
    {
      question: "Can you fix a GoHighLevel system another freelancer built?",
      answer:
        "Yes. I inherit and rebuild broken systems with a documented audit first — mapping every workflow, field, and pipeline before any changes, so nothing breaks during the transition.",
    },
    {
      question: "Do you work with multi-location franchises?",
      answer:
        "Yes. I build per-location pipelines, roll-up reporting, and brand-consistent snapshots so every franchise location runs the same architecture while reporting rolls up to headquarters.",
    },
    {
      question: "How do we get started without disrupting our live operations?",
      answer:
        "Every engagement begins with an audit and staging blueprint. We test all workflows, custom fields, and snapshots in a sandbox environment before rollout — ensuring your active campaigns and daily sales operations run 100% uninterrupted.",
    },
  ],
  faqCtaCopy: "Still deciding? Let's talk it through on a free call.",
  faqCtaLabel: "Book a Free Call",
};

// Re-export commonly used icons for convenience
export {
  ArrowRight,
  Gift,
  Clock,
  Plug,
  Layers,
  Workflow,
  BarChart3,
  Database,
  Zap,
  Package,
  ShieldCheck,
};

export type * from "./service-platform-types";
